import { Link } from "@tanstack/react-router";
import type { OnboardingStep } from "@finances/shared";
import styles from "./Checklist.module.css";
import { complete, current, detail, ordered, position, STEPS, visible } from "./steps.js";
import { useDismissOnboarding, useOnboarding } from "../../lib/queries.js";
import { useSession } from "../../lib/session.js";
import { Button, Card, ProgressBar, Row, Stack, Text } from "../../ui/index.js";

/**
 * What to do next, and how far through it you are.
 *
 * It sends people to the real screens rather than reimplementing them in a
 * wizard: the account form, the importer, the rule dialog and the budget editor
 * are already the best versions of themselves, and a parallel set inside a
 * takeover flow would be a second set to keep in step.
 *
 * Nothing here is a tutorial that can be got wrong — each step is ticked
 * because the household has the thing, so wandering off, finishing a step from
 * somewhere else, or coming back a week later on a different device all land in
 * the right place.
 */
export function Checklist() {
  const { householdId, householdName } = useSession();
  const query = useOnboarding(householdId);
  const dismiss = useDismissOnboarding(householdId);

  // Silent while it loads and if it fails: a checklist is help, and help that
  // renders an error where the month's figures should be is not help.
  if (!query.data || !visible(query.data)) return null;

  const progress = query.data;
  const { done, total } = position(progress);
  const next = current(progress);
  const finished = complete(progress);

  return (
    <Card
      title="Getting started"
      action={
        <Text size="sm" tone="secondary">
          {finished ? "All done" : `${String(done)} of ${String(total)}`}
        </Text>
      }
    >
      <Stack gap={4}>
        <ProgressBar value={done} target={total} />

        {finished ? (
          <Text tone="secondary">
            That is {householdName} set up - accounts, transactions, rules and a budget. You're all
            ready to go!
          </Text>
        ) : (
          <ol className={styles.steps}>
            {ordered(progress).map((step) => (
              <Step key={step.id} step={step} asking={step.id === next?.id} />
            ))}
          </ol>
        )}

        <Row justify="end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dismiss.mutate()}
            loading={dismiss.isPending}
          >
            {finished ? "Done" : "Skip for now"}
          </Button>
        </Row>
      </Stack>
    </Card>
  );
}

/** Only the step being asked for carries its explanation and its button. */
function Step({ step, asking }: { step: OnboardingStep; asking: boolean }) {
  const copy = STEPS[step.id];
  const note = detail(step);

  return (
    <li className={styles.step}>
      <span className={step.done ? styles.markerDone : styles.marker} aria-hidden="true">
        {step.done && (
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m3.5 8.5 3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>

      <div className={styles.body}>
        <Row gap={2} align="baseline" wrap>
          <Text weight={asking ? "semibold" : "normal"} tone={step.done ? "secondary" : "primary"}>
            {copy.label}
          </Text>
          <span className="visually-hidden">{step.done ? "Done" : "Still to do"}</span>
          {note && (
            <Text size="sm" tone="muted">
              {note}
            </Text>
          )}
        </Row>

        {asking && (
          <Stack gap={3} className={styles.asking}>
            <Text size="sm" tone="secondary">
              {copy.body}
            </Text>
            <Link to={copy.to} className={styles.action}>
              <Button variant="primary" size="sm">
                {copy.cta}
              </Button>
            </Link>
          </Stack>
        )}
      </div>
    </li>
  );
}
